import { AutocompleteWithLabel } from '@adotel/components'
import { useLazyQuery } from '@apollo/client'
import React, { useEffect, useState } from 'react'

import { ACCOMMODATIONS_QUERY } from '../../graphql/accommodation'
import { TENANTS_QUERY } from '../../graphql/tenant'
import {
  getDefaultCurrency,
  getEntityCode,
  getRoleFromStorage
} from '../../helpers/userData'
import { getTranslate } from '../../localization'
import inventoryPagesRoleManagement from '../../staticRoleManagement/inventoryRoleManagement'
import { Accommodation } from '../../types/accommodation'
import { GetInventoryInput } from '../../types/inventory'
import { Tenant } from '../../types/tenants'
import useStyles from './styles/InventoryBasicFilters.styles'

interface InventoryBasicFiltersProps {
  autoCompleteLabelAccommodation: any
  setautoCompleteLabelAccommodation: React.Dispatch<any>
  getInventoryInput: GetInventoryInput
  setgetInventoryInput: React.Dispatch<React.SetStateAction<GetInventoryInput>>
  setsecondaryFilterIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  isRoleChannel: boolean | undefined
  isRoleProvider: boolean | undefined
}

const InventoryBasicFilters = ({
  autoCompleteLabelAccommodation,
  setautoCompleteLabelAccommodation,
  getInventoryInput,
  setgetInventoryInput,
  setsecondaryFilterIsOpen,
  isRoleChannel,
  isRoleProvider
}: InventoryBasicFiltersProps) => {
  const classes = useStyles()
  const [autoCompleteLabelTenant, setautoCompleteLabelTenant] = useState<any>()

  const hasAccessToChangeTenant = inventoryPagesRoleManagement
    .find((item) => item.route === '/inventory/:page')
    ?.sections?.SELECT_TENANT.rolesListWithWriteAccess?.includes(
      getRoleFromStorage()
    )

  const hasAccessToChangeAccommodation = inventoryPagesRoleManagement
    .find((item) => item.route === '/inventory/:page')
    ?.sections?.SELECT_ACCOMMODATION.rolesListWithWriteAccess?.includes(
      getRoleFromStorage()
    )

  const isTenant = inventoryPagesRoleManagement
    .find((item) => item.route === '/inventory/:page')
    ?.sections?.IS_TENANT.rolesListWithWriteAccess?.includes(
      getRoleFromStorage()
    )

  const [getTenants, { data: tenantsData, loading: loadingForTenantsList }] =
    useLazyQuery<{
      tenants: Tenant[]
    }>(TENANTS_QUERY)

  useEffect(() => {
    if (hasAccessToChangeTenant) {
      getTenants()
    }
  }, [])

  const [getAccommodations, { data: accommodationsData }] = useLazyQuery<{
    accommodations: Accommodation[]
  }>(ACCOMMODATIONS_QUERY, { notifyOnNetworkStatusChange: true })

  useEffect(() => {
    if (hasAccessToChangeAccommodation) {
      getAccommodations()
    }
  }, [])

  return (
    <div
      className={[classes.root, isTenant ? classes.rootForRoleTenant : ''].join(
        ' '
      )}>
      <div>
        {hasAccessToChangeTenant && (
          <AutocompleteWithLabel
            value={autoCompleteLabelTenant}
            label={getTranslate('Tenant')}
            placeholder={getTranslate('Select accommodation tenant')}
            includeInputInList
            onChange={(_, v: any) => {
              if (v) {
                setautoCompleteLabelTenant({
                  value: v.value,
                  label: v.label,
                  code: v.code
                })
                getAccommodations({
                  variables: {
                    tenantId: v.value
                  }
                })
              } else {
                setautoCompleteLabelTenant(null)
              }
              setautoCompleteLabelAccommodation(null)
              setgetInventoryInput({
                ...getInventoryInput,
                propertyCode: '',
                channelID: isRoleChannel ? getInventoryInput.channelID : '',
                Currency: getDefaultCurrency(),
                providerCode: isRoleProvider
                  ? getInventoryInput.providerCode
                  : '',
                ratePlanCode: '',
                roomTypeCode: ''
              })
              setsecondaryFilterIsOpen(false)
            }}
            getOptionSelected={(option, value) =>
              option.value === value.value && option.label === value.label
            }
            getOptionLabel={(option) => option.label || ''}
            options={
              tenantsData?.tenants.map((item) => ({
                label: item.basicDetails.name,
                value: item.basicDetails?.tenantId || '',
                code: item.basicDetails?.displayTenantCode || ''
              })) || []
            }
            renderOption={(option) => <>{option.label}</>}
            size="small"
            inputSize="small"
            loading={loadingForTenantsList}
            loadingText={getTranslate('Loading') + '...'}
          />
        )}
      </div>
      <div>
        {hasAccessToChangeAccommodation && (
          <AutocompleteWithLabel
            label={getTranslate('Accommodation')}
            placeholder={getTranslate('Select accommodation')}
            includeInputInList
            onChange={(event, v: any) => {
              if (v) {
                setautoCompleteLabelAccommodation({
                  value: v.value,
                  label: v.label,
                  code: v.code
                })
                setgetInventoryInput({
                  ...getInventoryInput,
                  propertyCode: v.code,
                  channelID: isRoleChannel ? getInventoryInput.channelID : '',
                  Currency: getDefaultCurrency(),
                  providerCode: isRoleProvider
                    ? getInventoryInput.providerCode
                    : '',
                  ratePlanCode: '',
                  roomTypeCode: ''
                })
              } else {
                setautoCompleteLabelAccommodation(null)
                setgetInventoryInput({
                  ...getInventoryInput,
                  propertyCode: '',
                  channelID: isRoleChannel ? getInventoryInput.channelID : '',
                  Currency: getDefaultCurrency(),
                  providerCode: isRoleProvider
                    ? getInventoryInput.providerCode
                    : '',
                  ratePlanCode: '',
                  roomTypeCode: ''
                })
              }
              setsecondaryFilterIsOpen(false)
            }}
            getOptionSelected={(option, value) =>
              option.value === value.value && option.label === value.label
            }
            getOptionLabel={(option) => option.label || ''}
            options={
              accommodationsData
                ? accommodationsData?.accommodations
                    .filter((acc) =>
                      !hasAccessToChangeTenant
                        ? acc.basicDetails.displayTenantCode ===
                          (getEntityCode() || '')
                        : acc.basicDetails.displayTenantCode ===
                          autoCompleteLabelTenant?.code
                    )
                    .map((item) => ({
                      label: item.basicDetails.name,
                      value: item.basicDetails.accommodationID || '',
                      code: item.basicDetails.propertyCode
                    }))
                : []
            }
            value={autoCompleteLabelAccommodation}
            renderOption={(option) => <>{option.label}</>}
            size="small"
            inputSize="small"
            loading={loadingForTenantsList}
            loadingText={getTranslate('Please wait...')}
          />
        )}
      </div>
    </div>
  )
}

export default InventoryBasicFilters
